// This application pack is designed to be loaded for every page in the app (public, operator, crew, etc).
// But eventually it won't be loaded into online apps or native app webviews.

// - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -

// Import Rankers helper functions
// Helpers are small utility functions.
// These functions must be imported and then exposed on the window object so the can continue to be accessed via the global scope.
// We should nest them inside the `window.RankersHelpers` object to avoid collisions.
window.RankersHelpers ??= {};

import { createCookie, readCookie, eraseCookie } from '../rankers/helpers/cookies'
window.RankersHelpers.createCookie = createCookie;
window.RankersHelpers.readCookie = readCookie;
window.RankersHelpers.eraseCookie = eraseCookie;

import { dateFormatForDB } from '../rankers/helpers/date'
window.RankersHelpers.dateFormatForDB = dateFormatForDB;

import { isElementVisibleInViewport , isElementPartiallyInViewport, isElementDisplayedOnPage} from "../rankers/helpers/element_in_viewport";
window.RankersHelpers.isElementVisibleInViewport = isElementVisibleInViewport;
window.RankersHelpers.isElementPartiallyInViewport = isElementPartiallyInViewport;
window.RankersHelpers.isElementDisplayedOnPage = isElementDisplayedOnPage;

import { guidGenerator } from '../rankers/helpers/guid';
window.RankersHelpers.guidGenerator = guidGenerator;

import { injectStyles } from "../rankers/helpers/injection";
window.RankersHelpers.injectStyles = injectStyles;

import { isNumber } from '../rankers/helpers/number';
window.RankersHelpers.isNumber = isNumber;

// - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -

// Import Rankers library classes
// Library classes are useful tools that are more than just a simple helper function.
// These classes are imported and then exposed on the window object so they can be accessed via the global scope whenever application.js is loaded.
// We nest them inside the `window.RankersLibrary` object to avoid collisions.
window.RankersLibrary = {};

// - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -

// Import Rankers components
// Components are behaviour tied to a DOM element.

import { templatorLoader } from '../rankers/components/templator/templator_loader';
templatorLoader();

import { analyticsListenerLoader } from '../rankers/components/analytics_listener/analytics_listener_loader';
analyticsListenerLoader();

import { analyticsWatcherLoader } from '../rankers/components/analytics_watcher/analytics_watcher_loader';
analyticsWatcherLoader();

import { pageHeadingLoader } from '../rankers/components/page_heading/page_heading_loader';
pageHeadingLoader();

import { dropdownLoader } from '../rankers/components/dropdown/dropdown_loader';
dropdownLoader();

import { imageMetadataLoader } from '../rankers/components/image_metadata/image_metadata_loader';
imageMetadataLoader();

import { popoverLoader } from '../rankers/components/popover/popover_loader';
popoverLoader();

import { signupFormLoader } from '../rankers/components/signup_form/loader';
signupFormLoader();

import { modalizerLoader } from '../rankers/components/modalizer/modalizer_loader';
modalizerLoader();

import { breakpointerLoader } from '../rankers/components/breakpointer/breakpointer_loader';
breakpointerLoader();

import { togglerBtnsLoader } from '../rankers/components/toggler_btns/toggler_btns_loader';
togglerBtnsLoader();

import { togglerLinkLoader } from '../rankers/components/toggler_link/toggler_link_loader';
togglerLinkLoader();

import { reviewLoader } from '../rankers/components/review/review_loader';
reviewLoader();

import { avatarLoader } from '../rankers/components/avatar/avatar_loader';
avatarLoader();

import { productIntroLoader } from '../rankers/components/product_intro/product_intro_loader';
productIntroLoader();

import { inventoryLoader } from '../rankers/components/inventory/inventory_loader';
inventoryLoader();

import { charCounterLoader } from '../rankers/components/char_counter/char_counter_loader';
charCounterLoader();

import { crewMessagerLoader } from '../rankers/components/crew_messager/crew_messager_loader';
crewMessagerLoader();

import { externalFormLoader } from '../rankers/components/external_form/external_form_loader';
externalFormLoader();

import { collapsableFieldsetLoader } from '../rankers/components/collapsable_fieldset/collapsable_fieldset_loader';
collapsableFieldsetLoader();

import { numericPriceLoader } from '../rankers/components/numeric_price/numeric_price_loader';
numericPriceLoader();

import { imageAttacherLoader } from '../rankers/components/image_attacher/image_attacher_loader';
imageAttacherLoader();

import { selecterLoader } from '../rankers/components/selecter/selecter_loader';
selecterLoader();

import { directionsBtnLoader } from '../rankers/components/directions_btn/directions_btn_loader';
directionsBtnLoader();

import {csBubbleLoader} from "../rankers/components/cs_bubble/cs_bubble_loader";
csBubbleLoader();

import {profileBannerLoader} from "../rankers/components/profile_banner/profile_banner_loader";
profileBannerLoader();

import {collapserLoader} from "../rankers/components/collapser/collapser_loader";
collapserLoader();

import { promocodeLoader } from '../rankers/components/promocode/promocode_loader';
promocodeLoader();

import { clipboardCopyLoader } from '../rankers/components/clipboard_copy/clipboard_copy_loader';
clipboardCopyLoader();

import { offerBadgeLoader } from '../rankers/components/offer_badge/offer_badge_loader';
offerBadgeLoader();

import { hvidLoader } from '../rankers/components/hvid/hvid_loader';
hvidLoader();

import { moreFetcherLoader } from '../rankers/components/more_fetcher/more_fetcher_loader';
moreFetcherLoader();

import { gotoLoader } from '../rankers/components/goto/goto_loader';
gotoLoader();

import { starRatingLoader } from '../rankers/components/star_rating/star_rating_loader';
starRatingLoader();

import { ratingsGraphLoader } from "../rankers/components/ratings_graph";
ratingsGraphLoader();

// Registered globally rather than only in the enquire pack: `.enquire_option` markup
// also appears outside `_booking_avenues.html.erb` (the details "Websites" row, and the
// profile's general CTAs), and those pages never receive the inline enquire bundle — so
// the component never ran and their selection events were silently never emitted.
// Double-registration is safe: the initializer only constructs elements lacking the
// `--inited` class, so whichever pack runs first wins and the other skips.
// See plans/2026-08-24--global-enquiry-cta-avenue-analytics.md.
import { enquireOptionLoader } from '../rankers/inquiry/components/enquire_option/enquire_option_loader';
enquireOptionLoader();

// - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -

// Import other component like modules.
// These don't use the `Component` class as a base.
// Although these are exposed on the window object (within the window.RankersModules namespace) they are generally not called outside this pack (instantiation is all that is required).
window.RankersModules = {};

import LazyLoader from '../rankers/components/lazy_loader';
window.RankersModules.lazyLoader = new LazyLoader();

// - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -
